import React, { useEffect, useMemo, useState } from 'react';
import { useChild, ChildSummary } from '../../contexts/ChildContext';
import { parentApi } from '../../services/api';
import { BusyRange, overlapsBusy, formatBusyRanges } from '../../utils/appointmentAvailability';
import { awaitingChildAppointmentsText } from '../../utils/classStatus';
import FilterPills from '../../components/FilterPills';

const F = { fontFamily: 'Inter' } as const;

const STATUS_LABEL: Record<string, string> = {
  pending: 'Onay Bekliyor', pending_parent_approval: 'Onayınız Bekleniyor', confirmed: 'Onaylandı', cancelled: 'İptal Edildi', completed: 'Tamamlandı',
};
type StatusFilter = 'all' | 'pending' | 'confirmed' | 'cancelled' | 'completed';
const FILTER_LABEL: Record<StatusFilter, string> = { all: 'Tümü', pending: 'Onay Bekleyen', confirmed: 'Onaylanan', cancelled: 'İptal Edilen', completed: 'Tamamlanan' };
const FORMAT_LABEL: Record<string, string> = {
  face_to_face: 'Yüz Yüze', online: 'Online', phone: 'Telefon',
};

interface TeacherOpt {
  id: string;
  uuid: string;
  firstName: string;
  lastName: string;
  subjects: { id: number; name: string }[];
}

interface AppointmentCard {
  id: number | string;
  studentName: string;
  teacherName: string;
  subjectName: string | null;
  meetingType: string;
  location: string | null;
  status: string;
  statusLabel: string;
  statusColor: string;
  time: string;
  date: string;
  isOnline: boolean;
  format: string;
  notes: string | null;
}

function statusColor(s: string): string {
  if (s === 'confirmed') return '#11d600';
  if (s === 'pending') return '#f1972f';
  if (s === 'pending_parent_approval') return '#3973bf';
  if (s === 'cancelled') return '#e5599b';
  return '#888';
}

function mapAppointment(a: any): AppointmentCard {
  const d = a.appointmentDate ? new Date(a.appointmentDate) : null;
  const teacher = a.users_appointments_teacher_user_idTousers;
  const student = a.users_appointments_student_user_idTousers;
  const format = a.format ?? 'face_to_face';
  // start_time is a DB Time(0) field — extract UTC hours/minutes to avoid
  // local timezone shifting the displayed value (e.g. 00:00 UTC → 03:00 TR).
  const fmtTime = (s: string | null | undefined) => {
    if (!s) return null;
    const dt = new Date(s);
    return dt.getUTCHours().toString().padStart(2, '0') + ':' + dt.getUTCMinutes().toString().padStart(2, '0');
  };
  return {
    id: a.id,
    studentName: student ? `${student.first_name ?? ''} ${student.last_name ?? ''}`.trim() : 'Öğrenci',
    teacherName: teacher ? `${teacher.first_name ?? ''} ${teacher.last_name ?? ''}`.trim() : 'Öğretmen',
    subjectName: a.subjects?.name ?? null,
    meetingType: FORMAT_LABEL[format] ?? format,
    location: a.location ?? null,
    status: a.status ?? 'pending',
    statusLabel: STATUS_LABEL[a.status ?? ''] ?? (a.status ?? '-'),
    statusColor: statusColor(a.status ?? ''),
    time: fmtTime(a.start_time) ?? '-',
    date: d ? d.toLocaleDateString('tr-TR') : '-',
    isOnline: format === 'online',
    format,
    notes: a.notes ?? null,
  };
}

const CloseX: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick} className="absolute top-5 right-5 w-[44px] h-[44px] rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" /></svg>
  </button>
);

type ModalStep = null | 'child' | 'teacher' | 'datetime' | 'done' | 'error';

/**
 * Parent Appointments — lists real /parent/appointments rows and drives the
 * create-request flow:
 *
 *   1. Pick a child (skipped if only one).
 *   2. Pick a teacher from that child's active classes
 *      (GET /parent/children/:id/teachers).
 *   3. Pick date / start time / end time / format / notes.
 *   4. POST /parent/appointments — creates a `pending` appointment so the
 *      teacher receives the request on their own Randevularım page.
 */
const ParentAppointments: React.FC = () => {
  const { children: kids, loading: childLoading } = useChild();
  const [appointments, setAppointments] = useState<AppointmentCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [formatFilter, setFormatFilter] = useState<'all' | 'face_to_face' | 'online' | 'phone'>('all');

  const [modalStep, setModalStep] = useState<ModalStep>(null);
  const [modalChild, setModalChild] = useState<ChildSummary | null>(null);
  const [modalTeacher, setModalTeacher] = useState<TeacherOpt | null>(null);
  const [modalSubjectId, setModalSubjectId] = useState<number | null>(null);
  const [teachersList, setTeachersList] = useState<TeacherOpt[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('09:30');
  const [format, setFormat] = useState<'face_to_face' | 'online' | 'phone'>('face_to_face');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // B7: teacher's already-booked ranges for the picked date.
  const [busy, setBusy] = useState<BusyRange[]>([]);
  const [busyLoading, setBusyLoading] = useState(false);
  const [busyError, setBusyError] = useState(false);
  const conflict = useMemo(() => overlapsBusy(startTime, endTime, busy), [startTime, endTime, busy]);

  useEffect(() => {
    if (!modalTeacher || !date) { setBusy([]); setBusyError(false); return; }
    let cancelled = false;
    setBusyLoading(true);
    setBusyError(false);
    parentApi.teacherAvailability(modalTeacher.id, date)
      .then((r) => { if (!cancelled) { setBusy(r.data?.busy ?? []); setBusyError(false); } })
      .catch(() => { if (!cancelled) { setBusy([]); setBusyError(true); } })
      .finally(() => { if (!cancelled) setBusyLoading(false); });
    return () => { cancelled = true; };
  }, [modalTeacher, date]);

  const load = () => {
    setLoading(true);
    parentApi.appointments()
      .then((res) => setAppointments((res.data ?? []).map(mapAppointment)))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const [respondingId, setRespondingId] = useState<string | number | null>(null);
  const respond = (id: string | number, action: 'approve' | 'decline') => {
    setRespondingId(id);
    parentApi.respondAppointment(id, action)
      .then(() => load())
      .catch((e: any) => alert(e?.response?.data?.error ?? 'İşlem başarısız.'))
      .finally(() => setRespondingId(null));
  };

  const resetModal = () => {
    setModalStep(null); setModalChild(null); setModalTeacher(null);
    setModalSubjectId(null); setTeachersList([]);
    setDate(''); setStartTime('09:00'); setEndTime('09:30');
    setFormat('face_to_face'); setNotes(''); setCreateError(null); setBusy([]);
  };

  const openCreate = () => {
    if (kids.length === 0) return;
    setCreateError(null);
    if (kids.length === 1) pickChild(kids[0]);
    else setModalStep('child');
  };

  const pickChild = (c: ChildSummary) => {
    setModalChild(c);
    setModalStep('teacher');
    setTeachersLoading(true);
    parentApi.teachersForChild(c.id)
      .then((res) => setTeachersList(res.data ?? []))
      .catch((e) => setCreateError(e?.response?.data?.error ?? 'Öğretmen listesi yüklenemedi'))
      .finally(() => setTeachersLoading(false));
  };

  const pickTeacher = (t: TeacherOpt) => {
    setModalTeacher(t);
    setModalSubjectId(t.subjects[0]?.id ?? null);
    setModalStep('datetime');
  };

  const submit = async () => {
    if (!modalChild || !modalTeacher || !date) { setCreateError('Tüm alanları doldurun.'); return; }
    setSubmitting(true); setCreateError(null);
    try {
      await parentApi.createAppointment({
        teacherUuid: modalTeacher.uuid,
        studentUuid: modalChild.id,
        subjectId: modalSubjectId ?? undefined,
        appointmentDate: `${date}T${startTime}:00`,
        startTime,
        endTime,
        format,
        notes: notes || undefined,
      });
      setModalStep('done');
      load();
    } catch (e: any) {
      setCreateError(e?.response?.data?.error ?? e.message ?? 'Randevu oluşturulamadı');
      setModalStep('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Randevularım</h1>
        <button onClick={openCreate} disabled={childLoading || kids.length === 0}
          className="rounded-[12px] px-5 py-4 flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-40"
          style={{ background: '#3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#fafafa" strokeWidth="3" strokeLinecap="round"><line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" /></svg>
          <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Randevu Oluştur</span>
        </button>
      </div>
      <p style={{ ...F, fontSize: 15, fontWeight: 500, color: '#2b2b2b', marginBottom: 20 }}>
        Randevu talebiniz öğretmene iletilir, onaylandıktan sonra bildirim alırsınız.
      </p>

      <div className="mb-5 space-y-3">
        <FilterPills
          label="Durum"
          tone="blue"
          value={statusFilter}
          onChange={setStatusFilter}
          options={(Object.keys(FILTER_LABEL) as StatusFilter[]).map((k) => ({ value: k, label: FILTER_LABEL[k] }))}
        />
        <FilterPills
          label="Format"
          tone="pink"
          value={formatFilter}
          onChange={setFormatFilter}
          options={[{ value: 'all', label: 'Tümü' }, { value: 'face_to_face', label: 'Yüz Yüze' }, { value: 'online', label: 'Online' }, { value: 'phone', label: 'Telefon' }]}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : (() => {
        const filtered = appointments.filter((a) => {
          if (statusFilter !== 'all' && a.status !== statusFilter) return false;
          if (formatFilter !== 'all' && a.format !== formatFilter) return false;
          return true;
        });
        if (filtered.length === 0) {
          return (
            <p className="text-center text-gray-400 py-16">
              {appointments.length === 0
                ? 'Henüz randevunuz yok.'
                : 'Filtrelerle eşleşen randevu bulunmuyor.'}
            </p>
          );
        }
        return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map(apt => (
            <div key={apt.id} className="bg-[#fafafa] rounded-[24px] border-2 border-blue-100 p-4 lg:p-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              {/* Header = öğrenci adı (sınıf değil), öğrenci göstergesi yok — öğrenci
                  Randevularım kartıyla aynı renk/kenarlık dili. */}
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="text-[16px] lg:text-[18px] font-bold text-[#2b2b2b] truncate">{apt.studentName}</h3>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded flex-shrink-0 ${
                  apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                  (apt.status === 'pending' || apt.status === 'pending_parent_approval') ? 'bg-yellow-100 text-yellow-700' :
                  apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>{apt.statusLabel}</span>
              </div>
              <div className="space-y-1 text-[13px] lg:text-[14px] text-[#2b2b2b] mb-3" style={F}>
                <p>Öğretmen: {apt.teacherName}</p>
                {apt.subjectName && <p>Ders: {apt.subjectName}</p>}
                <p>Görüşme: {apt.meetingType}</p>
                {apt.location && <p>Yer: {apt.location}</p>}
                <p>Saat: {apt.time}</p>
                <p>Tarih: {apt.date}</p>
              </div>
              {apt.notes && (
                <p className="text-[13px] italic text-gray-500 border-t border-gray-100 pt-2 line-clamp-2 break-words" style={F} title={apt.notes}>{apt.notes}</p>
              )}
              {apt.status === 'pending_parent_approval' && (
                <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => respond(apt.id, 'approve')}
                    disabled={respondingId === apt.id}
                    className="flex-1 rounded-[12px] py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    style={{ background: '#11d600', ...F }}
                  >Onayla</button>
                  <button
                    onClick={() => respond(apt.id, 'decline')}
                    disabled={respondingId === apt.id}
                    className="flex-1 rounded-[12px] py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    style={{ background: '#e5599b', ...F }}
                  >Reddet</button>
                </div>
              )}
            </div>
          ))}
        </div>
        );
      })()}

      {/* ===== CREATE FLOW ===== */}

      {modalStep === 'child' && (
        <ModalShell onClose={resetModal} title="Çocuk Seçin" subtitle="Hangi öğrenciniz için randevu talep edersiniz?">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {kids.map(c => (
              <button key={c.id} onClick={() => pickChild(c)}
                className="bg-[#fafafa] rounded-[16px] p-4 flex items-center gap-4 hover:shadow-md transition-shadow text-left">
                <div className="w-[48px] h-[48px] rounded-full bg-[#3973bf] overflow-hidden flex-shrink-0">
                  {/* AP62: çocuğun fotoğrafı randevu akışında da görünsün */}
                  <img src={c.avatarUrl || '/a-avatar.png'} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }} className="truncate">{c.name} {c.surname}</p>
                  {c.className && <p style={{ ...F, fontSize: 12, color: '#3973bf', fontWeight: 600 }}>{c.className}</p>}
                </div>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2"><path d="M7 4l6 6-6 6" /></svg>
              </button>
            ))}
          </div>
        </ModalShell>
      )}

      {modalStep === 'teacher' && (
        <ModalShell onClose={resetModal}
          title="Öğretmen Seçin"
          subtitle={modalChild ? `${modalChild.name} ${modalChild.surname} için görüşmek istediğiniz öğretmeni seçin.` : ''}
          back={kids.length > 1 ? () => setModalStep('child') : undefined}>
          {teachersLoading ? (
            <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
          ) : teachersList.length === 0 ? (
            modalChild?.classStatus === 'awaiting' ? (
              // Kurumlu ama sınıfsız: randevu is made with the class's teachers, so it
              // is closed (the backend refuses it too) — say why, not "no teacher".
              <p className="text-gray-500 text-[14px] py-6 px-2 text-center leading-relaxed">
                {awaitingChildAppointmentsText(modalChild.name)}
              </p>
            ) : (
              <p className="text-gray-400 text-[14px] py-6 text-center">Bu öğrenciye atanmış öğretmen bulunamadı.</p>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {teachersList.map(t => (
                <button key={t.id} onClick={() => pickTeacher(t)}
                  className="bg-[#fafafa] rounded-[16px] p-4 flex items-center gap-4 hover:shadow-md transition-shadow text-left">
                  <div className="w-[44px] h-[44px] rounded-full bg-[#e5599b] overflow-hidden flex-shrink-0">
                    <img src="/a-avatar.png" alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{ ...F, fontSize: 15, fontWeight: 700, color: '#2b2b2b' }} className="truncate">
                      {t.firstName} {t.lastName}
                    </p>
                    {t.subjects.length > 0 && (
                      <p style={{ ...F, fontSize: 12, color: '#3973bf', fontWeight: 600 }}>
                        {t.subjects.map(s => s.name).join(', ')}
                      </p>
                    )}
                  </div>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2"><path d="M7 4l6 6-6 6" /></svg>
                </button>
              ))}
            </div>
          )}
          {createError && <p className="text-red-500 text-[13px] mt-3">{createError}</p>}
        </ModalShell>
      )}

      {modalStep === 'datetime' && modalChild && modalTeacher && (
        <ModalShell onClose={resetModal}
          title="Randevu Detayları"
          subtitle={`${modalChild.name} • ${modalTeacher.firstName} ${modalTeacher.lastName}`}
          back={() => setModalStep('teacher')}>
          <div className="space-y-4">
            {modalTeacher.subjects.length > 1 && (
              <div>
                <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Ders</label>
                <select value={modalSubjectId ?? ''} onChange={e => setModalSubjectId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[14px]" style={{ border: '2px solid #3973bf', ...F }}>
                  {modalTeacher.subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Tarih <span style={{ color: '#e5599b' }}>*</span></label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)}
                  className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]" style={{ border: '2px solid #3973bf', ...F }} />
              </div>
              <div>
                <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Başlangıç</label>
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                  className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]" style={{ border: '2px solid #3973bf', ...F }} />
              </div>
              <div>
                <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Bitiş</label>
                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                  className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]" style={{ border: '2px solid #3973bf', ...F }} />
              </div>
            </div>
            {/* B7: teacher availability for the picked date */}
            {date && (
              <div className={`rounded-[12px] px-4 py-2.5 text-[13px] ${conflict ? 'bg-[#fdecef] border border-[#e5599b]' : busyError ? 'bg-[#fff8e1] border border-[#f5c518]' : 'bg-[#eef4ff] border border-[#3973bf]'}`} style={F}>
                {busyLoading ? (
                  <span className="text-gray-500">Öğretmenin uygunluğu kontrol ediliyor…</span>
                ) : busyError ? (
                  <span className="text-[#a97e00]">Öğretmenin uygunluğu şu anda kontrol edilemedi. Talebiniz gönderilirken saat çakışması olursa reddedilecektir.</span>
                ) : busy.length === 0 ? (
                  <span className="text-[#2b6a2b]">Öğretmenin o gün planlı randevusu yok, seçtiğiniz saat uygun.</span>
                ) : (
                  <>
                    <span className="font-semibold text-[#2b2b2b]">Öğretmenin dolu saatleri: </span>
                    <span className="text-[#2b2b2b]">{formatBusyRanges(busy)}</span>
                    {conflict && <p className="text-[#c53030] font-semibold mt-1">Seçtiğiniz saat dolu. Lütfen farklı bir saat seçin.</p>}
                  </>
                )}
              </div>
            )}
            <div>
              <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Format</label>
              <div className="flex gap-2">
                {(['face_to_face', 'online', 'phone'] as const).map(f => (
                  <button key={f} onClick={() => setFormat(f)} type="button"
                    className="flex-1 py-2 rounded-[10px] text-[13px] font-semibold transition-colors"
                    style={{ background: format === f ? '#e5599b' : '#e5e5e5', color: format === f ? '#fff' : '#2b2b2b', ...F }}>
                    {FORMAT_LABEL[f]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Not <span className="text-[#e5599b]">*</span></label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="Görüşme konusu veya ek bilgi…"
                className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[14px] resize-none"
                style={{ border: '2px solid #3973bf', ...F }} />
            </div>
            {createError && <p className="text-red-500 text-[13px]">{createError}</p>}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setModalStep('teacher')}
                className="flex-1 rounded-[12px] py-3 hover:opacity-90" style={{ background: '#e5e5e5' }}>
                <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>Geri</span>
              </button>
              <button onClick={submit} disabled={submitting || !date || !startTime || !endTime || !notes || conflict}
                className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50" style={{ background: '#3973bf' }}>
                <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>
                  {submitting ? 'Gönderiliyor…' : conflict ? 'Saat Dolu' : 'Randevu Talep Et'}
                </span>
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {modalStep === 'done' && (
        <ModalShell onClose={resetModal} title="" subtitle="" center>
          <div className="py-6 text-center">
            <div className="w-[120px] h-[120px] rounded-full bg-[#3973bf] flex items-center justify-center mx-auto mb-4">
              <svg width="70" height="55" viewBox="0 0 60 45" fill="none">
                <path d="M5 22L22 39L55 6" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p style={{ ...F, fontSize: 20, fontWeight: 700, color: '#2b2b2b', marginBottom: 8 }}>
              Randevu Talebiniz Gönderildi
            </p>
            <p style={{ ...F, fontSize: 14, color: '#888', marginBottom: 20 }}>
              Öğretmen talebinizi onayladıktan sonra bildirim alacaksınız.
            </p>
            <button onClick={resetModal}
              className="rounded-[12px] px-8 py-3 hover:opacity-90" style={{ background: '#3973bf' }}>
              <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Kapat</span>
            </button>
          </div>
        </ModalShell>
      )}

      {modalStep === 'error' && (
        <ModalShell onClose={resetModal} title="Hata" subtitle="" center>
          <div className="py-4 text-center">
            <p className="text-red-500 text-[14px] mb-4">{createError ?? 'Bir hata oluştu.'}</p>
            <button onClick={() => { setModalStep('datetime'); setCreateError(null); }}
              className="rounded-[12px] px-8 py-3 hover:opacity-90" style={{ background: '#3973bf' }}>
              <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Tekrar Dene</span>
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

/** Shared shell for the create-appointment steps — keeps sizing / close button consistent. */
const ModalShell: React.FC<{ onClose: () => void; title: string; subtitle?: string; back?: () => void; center?: boolean; children: React.ReactNode }> = ({ onClose, title, subtitle, back, center, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
    <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[780px] max-h-[90vh] overflow-y-auto p-7" style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
      <CloseX onClick={onClose} />
      {back && (
        <button onClick={back} className="absolute top-5 left-5 w-[40px] h-[40px] rounded-full hover:bg-gray-200 flex items-center justify-center">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
      )}
      {title && <h2 style={{ ...F, fontSize: 22, fontWeight: 700, color: '#3973bf', marginBottom: subtitle ? 4 : 16, textAlign: center ? 'center' : 'left', paddingLeft: back ? 48 : 0 }}>{title}</h2>}
      {subtitle && <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#2b2b2b', marginBottom: 20, textAlign: center ? 'center' : 'left', paddingLeft: back ? 48 : 0 }}>{subtitle}</p>}
      {children}
    </div>
  </div>
);

export default ParentAppointments;
